/**
 * platform/core/src/guidelines/RECIP10.js
 *
 * RECIP 1.0 (Response Evaluation Criteria in PSMA PET/CT, version 1.0)
 *   - Gafita A, et al. J Nucl Med 2022;63:1651-1658 (original)
 *   - Gafita A, et al. Radiology 2023;308:e222148 (visual)
 *   - Gafita A, et al. J Nucl Med 2025;66:673-675 (roadmap)
 *
 * v5 data-model notes (RECIP 1.0 re-arch):
 *   - trackingType is now 5 categories: 'blood-pool' / 'liver-reference' /
 *     'lesion' / 'new-lesion' / 'other'. PSMA(+)/(-) is not encoded in
 *     trackingType — it lives on roi.psmaStatusByTimepoint.
 *   - PSMA-VOL classification rule changed from PROMISE blood-pool threshold
 *     to RECIP 1.0 proper:
 *       Bone lesions:        SUVmean > boneSuvCutoff (absolute, 3.0)
 *       Soft-tissue lesions: SUVmean > (liverFormulaConstant / SUVmean_liver)
 *                                       × (SUVmean_liver + SD_liver)
 *   - blood-pool ROIs: decorative only (kept for legacy; no computation use).
 *   - liver-reference ROI: reader draws once on right liver lobe; drives
 *     soft-tissue threshold computation at each timepoint where it has SUV.
 *   - measurementTechnique = VOLUMETRIC because RECIP is volume-driven.
 *   - PR cutoff = -30%; PD cutoff = +20% vs reference (default NADIR).
 *   - newLesionConfirmation = N/A (PSMA PET has no flare phenomenon).
 *   - completeResponseConfirmation = N/A (CR = zero PSMA uptake at the tp).
 *
 * RECIP-specific extension fields:
 *   - pdReference            'BASELINE' | 'NADIR' | 'PREVIOUS'  (default NADIR)
 *   - newLesionAccounting    'CUMULATIVE' | 'PER_SCAN'           (default CUMULATIVE)
 *   - bloodPoolROINames      string[]  case-insensitive substring match (legacy)
 *   - liverROINames          string[]  case-insensitive substring match (new)
 *   - boneSuvCutoff          number    absolute SUVmean cutoff for bone (3.0)
 *   - liverFormulaConstant   number    constant c in the soft-tissue formula (4.30)
 *   - borderlineRatio        number    fraction (default 0.15 = ±15%)
 */
const guideline = {
  guidelineId: 'RECIP10',
  guidelineName: 'RECIP 1.0',
  measurementTechnique: 'VOLUMETRIC',

  /* RECIP has no target/non-target concept; kept for schema compat only. */
  maxNumberOfTargets: 999,
  maxNumberOfTargetsPerOrgan: 999,

  /* Diameter-based fields — unused (volumetric only). */
  measurableLongAxis: 0,
  measurableShortAxis: 0,
  measurableLongAxisOfLymphNode: 0,
  measurableShortAxisOfLymphNode: 0,
  measurableLongAxisOfNewLesion: 0,
  measurableLongAxisOfNewLesionHST: 0,
  highSliceThickness: 0,
  measurableShortAxisOfNewLesion: 0,
  smallDiameterChange: 0,
  absoluteDiameterChange: 0,
  absoluteSumOfDiameterChange: 0,
  minDiameterIncrease: 0,
  minDiameterDecrease: 0,

  /* Volumetric measurability. Volume threshold is set by SUV at segmentation
   * time, not a fixed mL value. */
  measurableVolume: 0,
  measurableVolumeOfLymphNode: 0,
  measurableVolumeOfNewLesion: 0,
  smallVolumeChange: 0,
  absoluteVolumeChange: 0,
  absoluteSumOfVolumeChange: 0,

  /* RECIP cutoffs (stored as fractions; UI converts to % for display). */
  minVolumeIncrease: 0.2, // PD: +20%
  minVolumeDecrease: 0.3, // PR: -30%

  /* No flare in PSMA PET → no confirmation scan needed. */
  newLesionConfirmation: 'NOT_APPLICABLE',
  newLesionDateOfProgression: 'USE_INITIAL_DATE',
  completeResponseConfirmation: 'NOT_APPLICABLE',

  newLesionAlert: 'DISABLED',
  overallResponseAlert: 'DISABLED',
  minStableDiseaseDuration: 42,

  /* ----- RECIP-specific extension fields ----- */
  pdReference: 'NADIR',
  newLesionAccounting: 'CUMULATIVE',

  /**
   * RECIP 1.0 PSMA-VOL classification thresholds (Gafita 2022 JNM Supplementary
   * + 2023 Radiology).
   *
   * boneSuvCutoff: absolute SUVmean threshold for bone lesions. A bone lesion
   *   with SUVmean > 3.0 is PSMA(+). Not configurable in the paper; exposed
   *   here for edge-case overrides.
   *
   * liverFormulaConstant: the constant c in:
   *   SUVthr_st = (c / SUVmean_liver) × (SUVmean_liver + SD_liver)
   *   Per Gafita 2022 supplementary: c = 4.30. Sanity-check expected values:
   *     liver mean=2.0, SD=0.3 → threshold ≈ 4.95
   *     liver mean=2.5, SD=0.5 → threshold ≈ 5.16
   *     liver mean=1.8, SD=0.4 → threshold ≈ 5.26
   */
  boneSuvCutoff: 3.0,
  liverFormulaConstant: 4.3,

  /**
   * ROIs whose name contains any of these strings (case-insensitive substring)
   * are auto-classified as 'liver-reference' when switching to RECIP 1.0.
   * Reader can still manually assign liver-reference via the Type dropdown.
   */
  liverROINames: [
    'right liver lobe',
    'liver reference',
    'liver',
    'right hepatic lobe',
  ],

  /**
   * ROIs whose name contains any of these strings (case-insensitive substring)
   * are auto-classified as 'blood-pool' when switching to RECIP 1.0.
   * blood-pool is now decorative only — kept for legacy data compatibility.
   * Reader can still manually assign blood-pool via the Type dropdown.
   */
  bloodPoolROINames: ['descending thoracic aorta', 'ascending aorta', 'aorta'],

  /**
   * Tumour SUVmean within ±borderlineRatio × threshold of the threshold is
   * flagged 'borderline'. Visual cue only (yellow halo) — classification still
   * applies; reader can override. 0.15 = ±15%.
   * Branch-aware: bone lesions use boneSuvCutoff; soft-tissue uses the liver
   * formula result. Computed in applyPsmaClassificationFromBridge.
   */
  borderlineRatio: 0.15,
};

export default guideline;
